import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { validateEmail, validateRequired } from '../../../utils/validators';
import { ROUTES } from '../../../config/constants';
import { initializePassword } from '../../../services/api'; // Importiere die Funktion
import './InitPasswordForm.css';

const InitPasswordForm = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        oldPassword: '',
        newPassword: '',
        confirmPassword: '',
    });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);

    const validatePassword = (password) => {
        const errors = [];

        if (password.length < 12) {
            errors.push('mindestens 12 Zeichen');
        }

        if (!/[A-Z]/.test(password)) {
            errors.push('mindestens einen Großbuchstaben');
        }

        if (!/[0-9]/.test(password)) {
            errors.push('mindestens eine Zahl');
        }

        if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?`~]/.test(password)) {
            errors.push('mindestens ein Sonderzeichen');
        }

        return {
            isValid: errors.length === 0,
            error: errors.length > 0 ? `Das Passwort muss ${errors.join(', ')} enthalten.` : ''
        };
    };

    const validateForm = () => {
        //console.log('validateForm() aufgerufen mit formData:', formData);
        const newErrors = {};

        // Email validation - validateEmail gibt nur boolean zurück
        if (!validateEmail(formData.email)) {
            newErrors.email = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
        }

        // Required validation - validateRequired gibt {isValid, error} zurück
        const oldPasswordValidation = validateRequired(formData.oldPassword, 'Altes Passwort');
        if (!oldPasswordValidation.isValid) {
            newErrors.oldPassword = oldPasswordValidation.error;
        }

        // Password strength validation - verwende die lokale validatePassword Funktion
        const newPasswordValidation = validatePassword(formData.newPassword);
        if (!newPasswordValidation.isValid) {
            newErrors.newPassword = newPasswordValidation.error;
        }

        // Password confirmation
        if (formData.newPassword && formData.confirmPassword && formData.newPassword !== formData.confirmPassword) {
            newErrors.confirmPassword = 'Die Passwörter stimmen nicht überein.';
        }

        if (!formData.confirmPassword) {
            newErrors.confirmPassword = 'Bitte bestätigen Sie Ihr neues Passwort.';
        }

        //console.log('Alle Errors:', newErrors);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // Clear error for this field
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setErrors({});

        try {
            const result = await initializePassword({
                email: formData.email,
                oldPassword: formData.oldPassword,
                newPassword: formData.newPassword,
            });

            if (result.success) {
                setSuccess(true);
                setErrors({});
                //console.log('Password successfully changed:', result.data);
            } else {
                // Handle API errors
                if (result.status === 404) {
                    setErrors({ email: 'Benutzer mit dieser E-Mail-Adresse wurde nicht gefunden.' });
                } else if (result.status === 401) {
                    setErrors({ oldPassword: 'Das alte Passwort ist nicht korrekt.' });
                } else if (result.status === 400) {
                    // Password validation or other client errors
                    if (result.error.includes('Passwort muss')) {
                        setErrors({ newPassword: result.error });
                    } else if (result.error.includes('unterscheiden')) {
                        setErrors({ newPassword: result.error });
                    } else {
                        setErrors({ general: result.error });
                    }
                } else if (result.status === 403) {
                    setErrors({ general: 'Ihr Benutzerkonto ist deaktiviert. Bitte wenden Sie sich an den Administrator.' });
                } else {
                    setErrors({ general: result.error || 'Ein unbekannter Fehler ist aufgetreten.' });
                }
            }
        } catch (err) {
            //console.error('Password initialization error:', err);
            setErrors({ general: 'Ein Netzwerkfehler ist aufgetreten. Bitte versuchen Sie es erneut.' });
        } finally {
            setLoading(false);
        }
    };

    const handleBackToLogin = () => {
        navigate(ROUTES.LOGIN || '/login');
    };

    if (success) {
        return (
            <div className="init-password-form">
                <div className="success-message">
                    <h3>Passwort erfolgreich geändert!</h3>
                    <p>
                        Ihr Passwort wurde erfolgreich geändert.
                        Sie können sich nun mit Ihrem neuen Passwort anmelden.
                    </p>
                    <button
                        type="button"
                        className="submit-button"
                        onClick={handleBackToLogin}
                    >
                        Zurück zum Login
                    </button>
                </div>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="init-password-form">
            <div className="form-header">
                <h2>Passwort ändern</h2>
                <p>Geben Sie Ihre E-Mail-Adresse und Ihr altes Passwort ein, um ein neues Passwort zu erstellen.</p>
                <div className="password-requirements">
                    <strong>Passwort-Anforderungen:</strong>
                    <ul>
                        <li>Mindestens 12 Zeichen lang</li>
                        <li>Mindestens einen Großbuchstaben (A-Z)</li>
                        <li>Mindestens eine Zahl (0-9)</li>
                        <li>Mindestens ein Sonderzeichen (!@#$%^&* etc.)</li>
                    </ul>
                </div>
            </div>

            <div className="form-group">
                <input
                    type="email"
                    name="email"
                    placeholder="E-Mail-Adresse"
                    value={formData.email}
                    onChange={handleChange}
                    className={errors.email ? 'error' : ''}
                    disabled={loading}
                />
                {errors.email && <span className="error-text">{errors.email}</span>}
            </div>

            <div className="form-group">
                <input
                    type="password"
                    name="oldPassword"
                    placeholder="Altes Passwort"
                    value={formData.oldPassword}
                    onChange={handleChange}
                    className={errors.oldPassword ? 'error' : ''}
                    disabled={loading}
                />
                {errors.oldPassword && <span className="error-text">{errors.oldPassword}</span>}
            </div>

            <div className="form-group">
                <input
                    type="password"
                    name="newPassword"
                    placeholder="Neues Passwort"
                    value={formData.newPassword}
                    onChange={handleChange}
                    className={errors.newPassword ? 'error' : ''}
                    disabled={loading}
                />
                {errors.newPassword && <span className="error-text">{errors.newPassword}</span>}
            </div>

            <div className="form-group">
                <input
                    type="password"
                    name="confirmPassword"
                    placeholder="Neues Passwort wiederholen"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={errors.confirmPassword ? 'error' : ''}
                    disabled={loading}
                />
                {errors.confirmPassword && <span className="error-text">{errors.confirmPassword}</span>}
            </div>

            {errors.general && (
                <div className="error-message">{errors.general}</div>
            )}

            <div className="form-actions">
                <button
                    type="button"
                    className="back-button"
                    onClick={handleBackToLogin}
                    disabled={loading}
                >
                    Zurück zum Login
                </button>

                <button
                    type="submit"
                    className="submit-button"
                    disabled={loading}
                >
                    {loading ? 'Wird geändert...' : 'Passwort ändern'}
                </button>
            </div>
        </form>
    );
};

export default InitPasswordForm;